import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CloudArrowUpIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import TextInput from '../../Common/TextInput';
import PhoneInput from '../../Common/PhoneInput';
import CustomSelect from '../../Common/CustomSelect';
import SearchableSelect from '../../Common/SearchableSelect';
import ConfirmationModal from '../../Common/ConfirmationModal';
import {
  getPublicCenterSelfRegMeta,
  submitPublicCenterSelfRegistration
} from '../../../services/crmApi';
import { uploadFilePublic } from '../../../services/fileApi';
import {
  normalizeSriLankanNic,
  isValidSriLankanNic,
  applyNicDerivedPersonalInfo,
  getPersonalInfoFromNic
} from '../../../utils/nicUtils';
import { validateStoredPhoneValue } from '../../../utils/phoneUtils';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' }
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const acceptFromFormats = (formats) => {
  if (!Array.isArray(formats) || !formats.length) return 'image/*,application/pdf';
  const parts = [];
  for (const f of formats) {
    const fmt = String(f || '').toLowerCase();
    if (fmt === 'pdf') parts.push('application/pdf');
    else if (fmt === 'jpg' || fmt === 'jpeg') parts.push('image/jpeg');
    else if (fmt === 'png') parts.push('image/png');
    else if (fmt === 'doc') parts.push('application/msword');
    else if (fmt === 'docx') {
      parts.push('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
    }
  }
  return parts.join(',') || 'image/*,application/pdf';
};

const DocUploadField = ({
  label,
  required = false,
  accept = 'image/*,application/pdf',
  hint,
  fileMeta,
  uploading,
  onUpload,
  onClear,
  error
}) => (
  <div>
    <label className="block text-sm font-semibold text-gray-800 mb-1.5">
      {label}
      {required ? <span className="text-red-500 ml-0.5">*</span> : null}
    </label>
    <div className={`flex justify-center px-4 py-5 border-2 border-dashed rounded-lg hover:border-indigo-400 transition-colors bg-white ${
      error ? 'border-red-300' : 'border-gray-300'
    }`}
    >
      <div className="text-center w-full">
        <CloudArrowUpIcon className="mx-auto h-9 w-9 text-gray-400" />
        <div className="mt-2">
          <label className={`cursor-pointer text-sm font-medium text-indigo-600 hover:text-indigo-500 ${uploading ? 'pointer-events-none opacity-60' : ''}`}>
            <span>{uploading ? 'Uploading…' : 'Upload a file'}</span>
            <input
              type="file"
              className="sr-only"
              accept={accept}
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) onUpload(file);
              }}
            />
          </label>
          <span className="text-sm text-gray-600"> or drag and drop</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {hint || (required ? 'Required — PNG, JPG, PDF up to 10MB' : 'Optional — PNG, JPG, PDF up to 10MB')}
        </p>
        {fileMeta?.fileId && (
          <div className="mt-3 inline-flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-1.5 max-w-full">
            <DocumentTextIcon className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">{fileMeta.fileName || 'Uploaded'}</span>
            <button type="button" onClick={onClear} className="text-gray-500 hover:text-red-600" aria-label="Remove file">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
    {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
  </div>
);

const emptyForm = () => ({
  subProductId: '',
  requirementsCategoryId: '',
  firstName: '',
  lastName: '',
  nameOnCertificate: '',
  email: '',
  phone: '',
  nationalId: '',
  passportNumber: '',
  dateOfBirth: '',
  gender: 'prefer-not-to-say',
  street: '',
  city: '',
  docsByType: {}
});

const PublicCenterSelfRegistration = () => {
  const params = useParams();
  const locationId = params.locationId || window.location.pathname.split('/').filter(Boolean).pop();
  const [meta, setMeta] = useState(null);
  const [subProducts, setSubProducts] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(emptyForm());
  const [screeningAnswers, setScreeningAnswers] = useState({});

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!locationId) {
        setLoadingMeta(false);
        setMetaError('Missing center id in registration link');
        return;
      }
      setLoadingMeta(true);
      setMetaError('');
      try {
        const res = await getPublicCenterSelfRegMeta(locationId);
        if (!active) return;
        const payload = res?.data?.data ?? null;
        const courses = Array.isArray(payload?.courses) ? payload.courses : [];
        if (!payload) {
          throw new Error('Empty center catalog response');
        }
        setMeta(payload);
        setSubProducts(courses);
      } catch (err) {
        if (!active) return;
        console.error('[PublicCenterSelfRegistration] meta load failed', err);
        const message = err.response?.data?.message || err.message || 'Center not found';
        setMetaError(message);
        toast.error(message);
        setMeta(null);
        setSubProducts([]);
      } finally {
        if (active) setLoadingMeta(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [locationId]);

  const selectedCourse = useMemo(
    () => subProducts.find((sp) => String(sp._id) === String(form.subProductId)) || null,
    [subProducts, form.subProductId]
  );

  const categoryOptions = useMemo(() => {
    const cats = selectedCourse?.requirementCategories || [];
    return cats.map((c) => ({ value: String(c._id), label: c.name }));
  }, [selectedCourse]);

  const screeningQuestions = useMemo(
    () => (Array.isArray(selectedCourse?.screeningQuestions) ? selectedCourse.screeningQuestions : []),
    [selectedCourse]
  );

  const visibleScreening = useMemo(() => {
    return screeningQuestions
      .map((question, originalIndex) => ({ question, originalIndex }))
      .sort((a, b) => (a.question.order || 0) - (b.question.order || 0))
      .filter(({ question }) => {
        if (!question?.categoryId) return true;
        return form.requirementsCategoryId
          && String(question.categoryId) === String(form.requirementsCategoryId);
      });
  }, [screeningQuestions, form.requirementsCategoryId]);

  const requiredDocuments = useMemo(() => {
    if (!selectedCourse) return [];
    const all = selectedCourse.requiredDocuments || [];
    const hasCategoryIds = all.some((d) => d.categoryId);
    if (!hasCategoryIds) return all;
    if (!form.requirementsCategoryId) {
      return all.filter((d) => !d.categoryId);
    }
    return all.filter(
      (d) => !d.categoryId || String(d.categoryId) === String(form.requirementsCategoryId)
    );
  }, [selectedCourse, form.requirementsCategoryId]);

  const isEligibilityGate = (question) => {
    if (!question) return false;
    if (question?.eligibility?.isEligibilityGate === true) return true;
    return question.questionType === 'yes_no' && question.isRequired && question?.eligibility?.isEligibilityGate !== false;
  };

  const normalizeEligibleOptions = (question) => {
    const raw = question?.eligibility?.eligibleOptions;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((o) => String(o ?? '').trim()).filter(Boolean);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === 'nationalId') {
      const normalized = normalizeSriLankanNic(value);
      const derived = applyNicDerivedPersonalInfo(
        { nationalId: normalized, dateOfBirth: form.dateOfBirth, gender: form.gender },
        { overwriteFromNic: true }
      );
      setForm((p) => ({
        ...p,
        nationalId: derived.nationalId || normalized,
        dateOfBirth: derived.dateOfBirth || p.dateOfBirth,
        gender: derived.gender || p.gender
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.nationalId;
        return next;
      });
      return;
    }
    if (name === 'subProductId') {
      const course = subProducts.find((sp) => String(sp._id) === String(value));
      const cats = course?.requirementCategories || [];
      setForm((p) => ({
        ...p,
        subProductId: value,
        requirementsCategoryId: cats.length === 1 ? String(cats[0]._id) : '',
        docsByType: {}
      }));
      setScreeningAnswers({});
      setErrors((prev) => {
        const next = { ...prev };
        delete next.subProductId;
        delete next.requirementsCategoryId;
        Object.keys(next).forEach((k) => {
          if (k.startsWith('doc_') || k.startsWith('screening_')) delete next[k];
        });
        return next;
      });
      return;
    }
    if (name === 'requirementsCategoryId') {
      setForm((p) => ({ ...p, requirementsCategoryId: value, docsByType: {} }));
      setScreeningAnswers({});
      setErrors((prev) => {
        const next = { ...prev };
        delete next.requirementsCategoryId;
        Object.keys(next).forEach((k) => {
          if (k.startsWith('doc_') || k.startsWith('screening_')) delete next[k];
        });
        return next;
      });
      return;
    }
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const uploadDoc = async (documentType, title, file, maxFileSize) => {
    if (!file) return;
    const limit = maxFileSize || 10 * 1024 * 1024;
    if (file.size > limit) {
      toast.error(`File must be under ${Math.round(limit / (1024 * 1024))}MB`);
      return;
    }
    setUploadingDoc(documentType);
    try {
      const res = await uploadFilePublic(file, {
        category: 'Educational Certificate',
        entityType: 'Student',
        title: title || documentType,
        description: `${documentType} for center self-registration`
      });
      const data = res.data?.data || {};
      const fileId = data.fileId || data._id || data.id;
      if (!fileId) throw new Error('Upload did not return a file id');
      const metaDoc = {
        documentType,
        title: title || documentType,
        fileId: String(fileId),
        fileName: data.originalName || file.name,
        mimeType: data.mimeType || file.type,
        fileUrl: data.url || data.fileUrl || ''
      };
      setForm((p) => ({
        ...p,
        docsByType: { ...p.docsByType, [documentType]: metaDoc }
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`doc_${documentType}`];
        return next;
      });
      toast.success('File uploaded');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploadingDoc(null);
    }
  };

  const validate = () => {
    const next = {};
    if (!form.subProductId) next.subProductId = 'Course is required';
    if (categoryOptions.length && !form.requirementsCategoryId) {
      next.requirementsCategoryId = 'Requirement category is required';
    }
    if (!String(form.firstName || '').trim()) next.firstName = 'First name is required';
    if (!String(form.nameOnCertificate || '').trim() && !String(form.firstName || '').trim()) {
      next.nameOnCertificate = 'Name on certificate is required';
    }
    const email = String(form.email || '').trim();
    if (!email) next.email = 'Email is required';
    else if (!EMAIL_RE.test(email)) next.email = 'Enter a valid email address';

    const phoneCheck = validateStoredPhoneValue(form.phone, 'LK', { required: true });
    if (!phoneCheck.isValid) {
      next.phone = phoneCheck.parsed?.rawValue
        ? 'Enter a valid phone number'
        : 'Phone is required';
    }

    const nic = String(form.nationalId || '').trim();
    if (nic && !isValidSriLankanNic(nic)) {
      next.nationalId = 'Enter a valid Sri Lankan NIC (old or new format)';
    }
    if (nic && isValidSriLankanNic(nic) && form.dateOfBirth) {
      const derived = getPersonalInfoFromNic(nic);
      if (derived?.dateOfBirth && derived.dateOfBirth !== form.dateOfBirth) {
        next.dateOfBirth = 'Date of birth does not match NIC';
      }
    }
    if (!form.dateOfBirth) next.dateOfBirth = 'Date of birth is required';
    if (!form.gender) next.gender = 'Gender is required';

    visibleScreening.forEach(({ question, originalIndex }) => {
      const answer = screeningAnswers[originalIndex];
      if (question.isRequired) {
        if (!answer || (Array.isArray(answer) && answer.length === 0) || String(answer).trim() === '') {
          next[`screening_${originalIndex}`] = 'This question is required';
          return;
        }
      }
      if (!answer || (Array.isArray(answer) && answer.length === 0)) return;
      if (!isEligibilityGate(question)) return;

      if (question.questionType === 'yes_no') {
        const eligibleYesNo = question?.eligibility?.eligibleYesNo === 'No' ? 'No' : 'Yes';
        if (answer !== eligibleYesNo) {
          next[`screening_${originalIndex}`] = 'You are not eligible for this program based on your answer';
        }
      } else if (question.questionType === 'single_choice') {
        const eligibleOptions = normalizeEligibleOptions(question);
        if (eligibleOptions.length > 0 && !eligibleOptions.includes(String(answer))) {
          next[`screening_${originalIndex}`] = 'You are not eligible for this program based on your answer';
        }
      } else if (question.questionType === 'multiple_choice') {
        const eligibleOptions = normalizeEligibleOptions(question);
        if (eligibleOptions.length > 0) {
          const selected = Array.isArray(answer) ? answer : [answer];
          const normalizedSelected = selected.map((a) => String(a ?? '').trim()).filter(Boolean);
          const hasAny = normalizedSelected.some((a) => eligibleOptions.includes(a));
          if (!hasAny) {
            next[`screening_${originalIndex}`] = 'You are not eligible for this program based on your answer';
          }
        }
      }
    });

    for (const doc of requiredDocuments) {
      const type = doc.documentType;
      if (!form.docsByType?.[type]?.fileId) {
        next[`doc_${type}`] = `${doc.title || type} is required`;
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setConfirmOpen(true);
  };

  const submitRegistration = async () => {
    setSubmitting(true);
    try {
      const documents = Object.values(form.docsByType || {})
        .filter((d) => d?.fileId)
        .map((d) => ({
          documentType: d.documentType,
          fileName: d.fileName,
          fileId: d.fileId,
          fileUrl: d.fileUrl || undefined,
          mimeType: d.mimeType
        }));

      const phoneCheck = validateStoredPhoneValue(form.phone, 'LK', { required: true });
      const screeningResponses = visibleScreening.map(({ question, originalIndex }) => ({
        questionId: question?._id,
        questionType: question?.questionType,
        categoryId: question?.categoryId || undefined,
        answer: screeningAnswers[originalIndex] ?? ''
      }));
      const payload = {
        subProductId: form.subProductId,
        requirementsCategoryId: form.requirementsCategoryId || undefined,
        screeningResponses,
        student: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          nameOnCertificate:
            form.nameOnCertificate.trim() || `${form.firstName} ${form.lastName}`.trim(),
          email: form.email.trim().toLowerCase(),
          phone: phoneCheck.normalizedValue || form.phone.trim(),
          nationalId: form.nationalId ? normalizeSriLankanNic(form.nationalId) : '',
          passportNumber: form.passportNumber.trim(),
          dateOfBirth: form.dateOfBirth || undefined,
          gender: form.gender,
          address: {
            street: form.street.trim(),
            city: form.city.trim()
          }
        },
        documents
      };
      const res = await submitPublicCenterSelfRegistration(locationId, payload);
      setConfirmOpen(false);
      setResult(res.data?.data || null);
      toast.success(
        res.data?.message ||
          'Registration received. It is held until the center volume batch is verified by SLIM.'
      );
    } catch (err) {
      const apiErrors = err.response?.data?.errors || {};
      if (apiErrors.email || apiErrors.nationalId || apiErrors.passportNumber) {
        setErrors((prev) => ({ ...prev, ...apiErrors }));
      }
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const courseOptions = subProducts.map((sp) => ({
    value: sp._id,
    label: `${sp.code || ''} — ${sp.name || ''}`.replace(/^ — | — $/g, '').trim() || sp.name || sp._id
  }));

  if (result) {
    const reg = result.registration || {};
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="max-w-3xl w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 space-y-3">
          <h1 className="text-xl font-semibold text-gray-900">Registration received</h1>
          <p className="text-sm text-gray-600">
            Your details are held under a temporary registration number. You are not yet on the CRM Students list.
            After the center submits its volume batch and SLIM verifies it, a permanent student number will be assigned.
          </p>
          <div className="rounded-lg bg-gray-50 p-4 text-sm space-y-1.5">
            <div>
              <span className="text-gray-500">Temporary reg #:</span>{' '}
              <strong className="font-mono">{reg.registrationNumber || '—'}</strong>
            </div>
            <div><span className="text-gray-500">Email:</span> {reg.student?.email || form.email}</div>
            <div><span className="text-gray-500">Status:</span> {reg.status || 'Pending verification'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 lg:p-10">
        <h1 className="text-2xl font-semibold text-gray-900">
          {meta?.locationName || 'Center'} — Student Self Registration
        </h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Submit your personal details and the documents required for your selected course. Your registration stays
          temporary until the center submits the list and SLIM verifies it.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-6" noValidate>
          <div>
            <SearchableSelect
              label="Course"
              name="subProductId"
              required
              value={form.subProductId}
              onChange={onChange}
              options={courseOptions}
              placeholder={
                loadingMeta
                  ? 'Loading courses…'
                  : (courseOptions.length ? 'Select course' : 'No courses available for this center')
              }
              disabled={loadingMeta || !courseOptions.length}
              error={errors.subProductId}
            />
            {metaError ? (
              <p className="text-xs text-red-600 mt-1">{metaError}</p>
            ) : null}
            {!loadingMeta && !metaError && !courseOptions.length && (
              <p className="text-xs text-amber-600 mt-1">
                No courses are published for this center yet. Ask the center to assign courses to this location.
              </p>
            )}
          </div>

          {categoryOptions.length > 0 && (
            <CustomSelect
              label="Requirement category"
              name="requirementsCategoryId"
              required
              value={form.requirementsCategoryId}
              onChange={onChange}
              options={categoryOptions}
              placeholder="Select requirement category"
              error={errors.requirementsCategoryId}
            />
          )}

          {visibleScreening.length > 0 && (
            <div className="space-y-4 pt-2 border-t border-gray-100">
              <div>
                <p className="text-sm font-semibold text-gray-900">Eligibility questions</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Answer the questions for your selected requirement category. Incorrect eligibility answers will block registration.
                </p>
              </div>
              <div className="space-y-4">
                {visibleScreening.map(({ question, originalIndex }, visibleIndex) => (
                  <div key={`${originalIndex}_${String(question?._id || '')}`} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-start space-x-2 mb-3">
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-xs font-medium">
                        {visibleIndex + 1}
                      </span>
                      <label className="block text-sm font-medium text-gray-900">
                        {question.question}
                        {question.isRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                    </div>

                    {question.questionType === 'text' && (
                      <input
                        type="text"
                        value={screeningAnswers[originalIndex] || ''}
                        onChange={(e) => {
                          setScreeningAnswers((prev) => ({ ...prev, [originalIndex]: e.target.value }));
                          setErrors((prev) => {
                            if (!prev[`screening_${originalIndex}`]) return prev;
                            const next = { ...prev };
                            delete next[`screening_${originalIndex}`];
                            return next;
                          });
                        }}
                        className={`w-full px-3 py-2 border ${errors[`screening_${originalIndex}`] ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    )}

                    {question.questionType === 'textarea' && (
                      <textarea
                        value={screeningAnswers[originalIndex] || ''}
                        onChange={(e) => {
                          setScreeningAnswers((prev) => ({ ...prev, [originalIndex]: e.target.value }));
                          setErrors((prev) => {
                            if (!prev[`screening_${originalIndex}`]) return prev;
                            const next = { ...prev };
                            delete next[`screening_${originalIndex}`];
                            return next;
                          });
                        }}
                        rows={4}
                        className={`w-full px-3 py-2 border ${errors[`screening_${originalIndex}`] ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    )}

                    {question.questionType === 'single_choice' && question.options && (
                      <div className="space-y-2">
                        {question.options.map((option, optIndex) => (
                          <label key={optIndex} className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name={`question_${originalIndex}`}
                              value={option}
                              checked={screeningAnswers[originalIndex] === option}
                              onChange={(e) => {
                                setScreeningAnswers((prev) => ({ ...prev, [originalIndex]: e.target.value }));
                                setErrors((prev) => {
                                  if (!prev[`screening_${originalIndex}`]) return prev;
                                  const next = { ...prev };
                                  delete next[`screening_${originalIndex}`];
                                  return next;
                                });
                              }}
                              className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {question.questionType === 'multiple_choice' && question.options && (
                      <div className="space-y-2">
                        {question.options.map((option, optIndex) => {
                          const selectedOptions = Array.isArray(screeningAnswers[originalIndex])
                            ? screeningAnswers[originalIndex]
                            : [];
                          return (
                            <label key={optIndex} className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                value={option}
                                checked={selectedOptions.includes(option)}
                                onChange={(e) => {
                                  const currentAnswers = Array.isArray(screeningAnswers[originalIndex])
                                    ? screeningAnswers[originalIndex]
                                    : [];
                                  const newAnswers = e.target.checked
                                    ? [...currentAnswers, option]
                                    : currentAnswers.filter((item) => item !== option);
                                  setScreeningAnswers((prev) => ({ ...prev, [originalIndex]: newAnswers }));
                                  setErrors((prev) => {
                                    if (!prev[`screening_${originalIndex}`]) return prev;
                                    const next = { ...prev };
                                    delete next[`screening_${originalIndex}`];
                                    return next;
                                  });
                                }}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">{option}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {question.questionType === 'yes_no' && (
                      <div className="flex gap-4">
                        {['Yes', 'No'].map((option) => (
                          <label key={option} className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name={`question_${originalIndex}`}
                              value={option}
                              checked={screeningAnswers[originalIndex] === option}
                              onChange={(e) => {
                                setScreeningAnswers((prev) => ({ ...prev, [originalIndex]: e.target.value }));
                                setErrors((prev) => {
                                  if (!prev[`screening_${originalIndex}`]) return prev;
                                  const next = { ...prev };
                                  delete next[`screening_${originalIndex}`];
                                  return next;
                                });
                              }}
                              className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {errors[`screening_${originalIndex}`] ? (
                      <p className="mt-2 text-xs text-red-600">{errors[`screening_${originalIndex}`]}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
            <TextInput
              variant="input"
              label="First name"
              name="firstName"
              value={form.firstName}
              onChange={onChange}
              required
              error={errors.firstName}
              placeholder="e.g. Chamath"
            />
            <TextInput
              variant="input"
              label="Last name"
              name="lastName"
              value={form.lastName}
              onChange={onChange}
              placeholder="e.g. Perera"
            />
            <div className="md:col-span-2">
              <TextInput
                variant="input"
                label="Name on certificate"
                name="nameOnCertificate"
                value={form.nameOnCertificate}
                onChange={onChange}
                placeholder="Exactly as on NIC (defaults to first + last name)"
              />
            </div>
            <TextInput
              variant="input"
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              required
              error={errors.email}
              placeholder="e.g. student@email.com"
            />
            <PhoneInput
              label="Phone"
              name="phone"
              value={form.phone}
              onChange={onChange}
              required
              error={errors.phone}
              placeholder="e.g. 771234567"
            />
            <TextInput
              variant="input"
              label="NIC"
              name="nationalId"
              value={form.nationalId}
              onChange={onChange}
              error={errors.nationalId}
              placeholder="e.g. 199012345678 or 901234567V"
            />
            <TextInput
              variant="input"
              label="Passport"
              name="passportNumber"
              value={form.passportNumber}
              onChange={onChange}
              placeholder="Optional — e.g. N1234567"
            />
            <TextInput
              variant="input"
              label="Date of birth"
              name="dateOfBirth"
              type="date"
              value={form.dateOfBirth}
              onChange={onChange}
              required
              error={errors.dateOfBirth}
              placeholder="YYYY-MM-DD"
            />
            <CustomSelect
              label="Gender"
              name="gender"
              value={form.gender}
              onChange={onChange}
              options={GENDER_OPTIONS}
              required
              error={errors.gender}
              placeholder="Select gender"
            />
            <TextInput
              variant="input"
              label="Street"
              name="street"
              value={form.street}
              onChange={onChange}
              placeholder="e.g. 12 Galle Road"
            />
            <TextInput
              variant="input"
              label="City"
              name="city"
              value={form.city}
              onChange={onChange}
              placeholder="e.g. Galle"
            />
          </div>

          <div className="space-y-4 pt-2 border-t border-gray-100">
            <div>
              <p className="text-sm font-semibold text-gray-900">Required documents</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {form.subProductId
                  ? (requiredDocuments.length
                    ? 'Upload each document required for the selected course.'
                    : 'This course has no document prerequisites configured.')
                  : 'Select a course to see required documents.'}
              </p>
            </div>
            {requiredDocuments.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requiredDocuments.map((doc) => (
                  <DocUploadField
                    key={doc.documentType}
                    label={doc.title || doc.documentType}
                    required
                    accept={acceptFromFormats(doc.acceptedFormats)}
                    fileMeta={form.docsByType?.[doc.documentType]}
                    uploading={uploadingDoc === doc.documentType}
                    error={errors[`doc_${doc.documentType}`]}
                    onUpload={(file) => uploadDoc(doc.documentType, doc.title, file, doc.maxFileSize)}
                    onClear={() => setForm((p) => {
                      const next = { ...p.docsByType };
                      delete next[doc.documentType];
                      return { ...p, docsByType: next };
                    })}
                  />
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || !!uploadingDoc}
            className="w-full sm:w-auto min-w-[220px] py-2.5 px-6 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
          >
            {submitting ? 'Submitting…' : 'Register now'}
          </button>
        </form>
      </div>

      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        onConfirm={submitRegistration}
        title="Confirm registration"
        message={`Submit your registration for ${selectedCourse?.name || 'this course'}? Your details will be held until the center submits its volume batch and SLIM verifies it.`}
        confirmText={submitting ? 'Submitting…' : 'Confirm & register'}
        cancelText="Review details"
        type="info"
        loading={submitting}
      />
    </div>
  );
};

export default PublicCenterSelfRegistration;
